import axios from 'axios';
import Cookies from 'js-cookie';
import { captureError, addBreadcrumb } from '../utils/sentry';

const API_URL = import.meta.env.VITE_API_URL;

if (!API_URL) {
  throw new Error('API_URL is not defined. Please set VITE_API_URL in your .env file.');
}

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = Cookies.get('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Add breadcrumb for Sentry
  addBreadcrumb(`API Request: ${config.method?.toUpperCase()} ${config.url}`, 'http', 'info', {
    url: config.url,
    method: config.method,
  });

  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => {
    // Add success breadcrumb for Sentry
    addBreadcrumb(
      `API Success: ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`,
      'http',
      'info',
      { status: response.status, url: response.config.url }
    );

    return response;
  },
  (error) => {
    // Log critical errors only
    if (error.response) {
      if (error.response.status === 307) {
        console.error(`[307 Redirect] Original URL: ${error.config?.url}`);
        console.error(
          `[307 Redirect] Redirect Location: ${error.response.headers?.location || 'Not provided'}`
        );
      }

      // Add error breadcrumb for Sentry
      addBreadcrumb(
        `API Error: ${error.response.status} ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
        'http',
        'error',
        {
          status: error.response.status,
          url: error.config?.url,
          data: error.response.data,
        }
      );

      // Capture significant errors (not auth errors which are expected)
      if (error.response.status >= 500) {
        captureError(error, {
          tags: { section: 'api_error' },
          extra: {
            url: error.config?.url,
            method: error.config?.method,
            status: error.response.status,
            responseData: error.response.data,
          },
        });
      }
    } else {
      console.error(`[API Network Error]`, error.message);

      // Add network error breadcrumb
      addBreadcrumb(`API Network Error: ${error.message}`, 'http', 'error', {
        message: error.message,
      });

      // Capture network errors
      captureError(error, {
        tags: { section: 'api_network_error' },
        extra: { message: error.message },
      });
    }

    if (error.response?.status === 401) {
      // Only redirect to login for non-push notification requests to prevent infinite loops
      const isPushNotificationRequest = error.config?.url?.includes('/notifications/push/');

      if (!isPushNotificationRequest) {
        Cookies.remove('access_token');
        window.location.href = '/login';
      } else {
        console.warn('[API] Authentication error for push notification request, not redirecting to prevent reload loop');
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email, password) => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);

    const response = await api.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  },

  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  updateCurrentUser: async (userData) => {
    const response = await api.put('/auth/me', userData);
    return response.data;
  },

  getPendingRequests: async () => {
    const response = await api.get('/auth/pending-requests');
    return response.data;
  },

  approveUser: async (userId) => {
    const response = await api.post(`/auth/approve-user/${userId}`);
    return response.data;
  },

  rejectUser: async (userId) => {
    const response = await api.delete(`/auth/reject-user/${userId}`);
    return response.data;
  },

  getAdminUsers: async () => {
    const response = await api.get('/admin/admin-users');
    return response.data;
  },

  promoteToAdmin: async (userId) => {
    const response = await api.post(`/admin/promote-to-admin/${userId}`);
    return response.data;
  },

  demoteFromAdmin: async (userId) => {
    const response = await api.post(`/admin/demote-from-admin/${userId}`);
    return response.data;
  },

  // Password reset functions
  requestPasswordReset: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await api.post('/auth/reset-password', {
      token,
      new_password: newPassword,
    });
    return response.data;
  },

  submitTrainingGroupApplication: async (applicationData) => {
    const response = await api.post('/auth/apply-training-group', applicationData);
    return response.data;
  },

  // Admin bulk approve users
  bulkApproveUsers: async (userIds) => {
    const response = await api.post('/auth/approve-users/bulk', userIds);
    return response.data;
  },
};

// Push Notifications API (deprecated; use notificationsAPI equivalents)

// Notifications API
export const notificationsAPI = {
  getPreferences: async () => {
    const response = await api.get('/notifications/preferences');
    return response.data;
  },

  updatePreferences: async (preferences) => {
    const response = await api.put('/notifications/preferences', preferences);
    return response.data;
  },

  subscribeToPush: async (subscriptionData) => {
    const response = await api.post('/notifications/push/subscribe', subscriptionData);
    return response.data;
  },

  unsubscribeFromPush: async (endpoint) => {
    const response = await api.delete('/notifications/push/unsubscribe', {
      params: { endpoint },
    });
    return response.data;
  },

  getPushSubscriptions: async () => {
    const response = await api.get('/notifications/push/subscriptions');
    return response.data;
  },

  getVapidPublicKey: async () => {
    const response = await api.get('/notifications/vapid-public-key');
    return response.data;
  },

  testPushNotification: async () => {
    const response = await api.post('/notifications/push/test');
    return response.data;
  },

  // Get user notifications
  getNotifications: async (limit = 50, offset = 0, unreadOnly = false) => {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    if (unreadOnly) params.append('unread_only', 'true');

    const response = await api.get(`/notifications?${params.toString()}`);
    return response.data;
  },

  // Get unread count
  getUnreadCount: async () => {
    const response = await api.get('/notifications/unread/count');
    return response.data;
  },

  // Mark notification as read
  markAsRead: async (notificationId) => {
    const response = await api.put(`/notifications/${notificationId}/read`);
    return response.data;
  },

  // Mark all notifications as read
  markAllAsRead: async () => {
    const response = await api.put('/notifications/mark-all-read');
    return response.data;
  },

  // Send custom notification
  sendCustomNotification: async (notificationData) => {
    const response = await api.post('/notifications/custom', notificationData);
    return response.data;
  },

  // Send availability reminder
  sendAvailabilityReminder: async (reminderData) => {
    const response = await api.post('/notifications/availability-reminder', reminderData);
    return response.data;
  },

  // Send admin announcement
  sendAdminAnnouncement: async (announcementData) => {
    const response = await api.post('/notifications/admin-announcement', announcementData);
    return response.data;
  },

  // Trigger scheduled notifications (admin only)
  triggerScheduleReminders: async () => {
    const response = await api.post('/notifications/schedule-reminders');
    return response.data;
  },

  triggerAvailabilityReminders: async (force = false) => {
    const response = await api.post('/notifications/availability-reminders', null, {
      params: { force }
    });
    return response.data;
  },

  // Send global announcement (admin only)
  sendGlobalAnnouncement: async (data) => {
    const response = await api.post('/notifications/admin-announcement', data);
    return response.data;
  },

  // Get notification logs (admin only)
  getNotificationLogs: async () => {
    const response = await api.get('/notifications/logs');
    return response.data;
  },

  // Admin endpoints
  adminSendTest: async (userId, title, body) => {
    const response = await api.post('/notifications/admin/send-test', null, {
      params: { user_id: userId, title, body },
    });
    return response.data;
  },

  adminGetUserSubscriptions: async (userId) => {
    const response = await api.get(`/notifications/admin/user-subscriptions/${userId}`);
    return response.data;
  },

  // Send test notification to specific user
  sendTestNotification: async (userId) => {
    const response = await api.post('/notifications/admin/test-notification', null, {
      params: { user_id: userId }
    });
    return response.data;
  },
};

// Availability API
export const availabilityAPI = {
  getAvailability: async (startDate = null, endDate = null) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await api.get('/availability/', { params });
    return response.data;
  },

  getWeeklyAvailability: async (weekStart) => {
    const response = await api.get(`/availability/week/${weekStart}`);
    return response.data;
  },

  getCrewmatesWeeklyAvailability: async (weekStart, { crewIds = [], userIds = [] } = {}) => {
    const params = new URLSearchParams();
    if (crewIds && crewIds.length) params.set('crew_ids', crewIds.join(','));
    if (userIds && userIds.length) params.set('user_ids', userIds.join(','));
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`/availability/week/${weekStart}/crewmates${query}`);
    return response.data;
  },

  setBulkAvailability: async (slots, start_date = null, end_date = null, replace_calendar_dates = null) => {
    const body = { slots: slots ?? [] };
    if (replace_calendar_dates?.length) {
      body.replace_calendar_dates = replace_calendar_dates;
    } else if (start_date != null && end_date != null) {
      body.start_date = start_date;
      body.end_date = end_date;
    }
    const response = await api.post('/availability/bulk', body);
    return response.data;
  },

  setWeeklyAvailability: async (weeklyData) => {
    const response = await api.post('/availability/weekly', weeklyData);
    return response.data;
  },

  createAvailability: async (availabilityData) => {
    const response = await api.post('/availability/', availabilityData);
    return response.data;
  },

  updateAvailability: async (id, updateData) => {
    const response = await api.put(`/availability/${id}`, updateData);
    return response.data;
  },

  deleteAvailability: async (id) => {
    const response = await api.delete(`/availability/${id}`);
    return response.data;
  },
};

// Training Sessions API
export const trainingSessionsAPI = {
  getSessions: async (params = {}) => {
    const {
      skip = 0,
      limit = 100,
      training_group_id,
      start_date,
      end_date,
      session_type,
      include_scheduled,
      for_autoplanner,
    } = params;

    const queryParams = { skip, limit };

    if (training_group_id !== undefined) queryParams.training_group_id = training_group_id;
    if (start_date !== undefined) queryParams.start_date = start_date;
    if (end_date !== undefined) queryParams.end_date = end_date;
    if (session_type !== undefined) queryParams.session_type = session_type;
    if (include_scheduled !== undefined) queryParams.include_scheduled = include_scheduled;
    if (for_autoplanner !== undefined) queryParams.for_autoplanner = for_autoplanner;

    const response = await api.get('/training-sessions/', {
      params: queryParams,
    });
    return response.data;
  },

  getSession: async (id) => {
    const response = await api.get(`/training-sessions/${id}`);
    return response.data;
  },

  createSession: async (sessionData) => {
    const response = await api.post('/training-sessions/', sessionData);
    return response.data;
  },

  updateSession: async (id, updateData) => {
    const response = await api.put(`/training-sessions/${id}`, updateData);
    return response.data;
  },

  deleteSession: async (id) => {
    const response = await api.delete(`/training-sessions/${id}`);
    return response.data;
  },

  // New endpoint for scheduling unscheduled sessions
  scheduleSession: async (id, scheduleData) => {
    const response = await api.post(`/training-sessions/${id}/schedule`, scheduleData);
    return response.data;
  },

  // Session splitting and management endpoints
  // Unified to planner route to avoid duplicate endpoints
  rescheduleSession: async (id) => {
    const response = await api.put(`/planning/sessions/${id}/reschedule`);
    return response.data;
  },

  getSessionGroup: async (groupId) => {
    const response = await api.get(`/training-sessions/group/${groupId}`);
    return response.data;
  },

  deleteSessionOrGroup: async (id, deleteEntireGroup = false) => {
    const params = { delete_entire_group: deleteEntireGroup };
    const response = await api.delete(`/training-sessions/${id}`, { params });
    return response.data;
  },

  // Position assignment endpoints
  assignPositions: async (id, assignments) => {
    const response = await api.post(`/training-sessions/${id}/assign-positions`, assignments);
    return response.data;
  },

  getUserAssignments: async (id) => {
    const response = await api.get(`/training-sessions/${id}/user-assignments`);
    return response.data;
  },

  // Get user's training groups with their crews
  getUserTrainingGroups: async () => {
    const response = await api.get('/training-sessions/user-training-groups');
    return response.data;
  },

  // Bulk import endpoints
  validateImportData: async (importData, selectedCrews) => {
    importData.selected_crews = selectedCrews;
    const response = await api.post('/training-sessions/import/validate', importData);
    return response.data;
  },

  bulkCreateSessions: async (sessionsData) => {
    const response = await api.post('/training-sessions/import/create', sessionsData);
    return response.data;
  },

  // Admin endpoint to get all sessions
  getAllSessionsAdmin: async () => {
    const response = await api.get('/training-sessions/admin/all');
    return response.data;
  },

  // Admin bulk delete sessions
  bulkDeleteSessions: async (sessionIds) => {
    const response = await api.delete('/training-sessions/admin/bulk', {
      data: sessionIds,
    });
    return response.data;
  },
};

// Equipment API
export const equipmentAPI = {
  getEquipment: async (availableOnly = false) => {
    const response = await api.get('/equipment/', {
      params: { available_only: availableOnly },
    });
    return response.data;
  },

  createEquipment: async (equipmentData) => {
    const response = await api.post('/equipment/', equipmentData);
    return response.data;
  },

  updateEquipment: async (id, updateData) => {
    const response = await api.put(`/equipment/${id}`, updateData);
    return response.data;
  },

  deleteEquipment: async (id) => {
    const response = await api.delete(`/equipment/${id}`);
    return response.data;
  },
};

// Crews API
export const crewsAPI = {
  getCrews: async (params = {}) => {
    const response = await api.get('/crews/', {
      params: { ...params },
    });
    return response.data;
  },

  // Admin endpoint to get all crews
  getAllCrewsAdmin: async (params = {}) => {
    const response = await api.get('/crews/admin/all', { params });
    return response.data;
  },

  getCrew: async (id) => {
    const response = await api.get(`/crews/${id}/`);
    return response.data;
  },

  createCrew: async (crewData) => {
    const response = await api.post('/crews/', crewData);
    return response.data;
  },

  updateCrew: async (id, updateData) => {
    const response = await api.put(`/crews/${id}/`, updateData);
    return response.data;
  },

  deleteCrew: async (id) => {
    const response = await api.delete(`/crews/${id}/`);
    return response.data;
  },

  addCrewMember: async (crewId, memberData) => {
    const response = await api.post(`/crews/${crewId}/members/`, memberData);
    return response.data;
  },

  removeCrewMember: async (crewId, memberId) => {
    const response = await api.delete(`/crews/${crewId}/members/${memberId}/`);
    return response.data;
  },

  // Admin bulk delete crews
  bulkDeleteCrews: async (crewIds) => {
    const response = await api.delete('/crews/admin/bulk', {
      data: crewIds,
    });
    return response.data;
  },
};

// Users API
export const usersAPI = {
  getUsers: async (params = {}) => {
    const response = await api.get('/auth/users', { params });
    return response.data;
  },

  getUser: async (id) => {
    const response = await api.get(`/auth/users/${id}`);
    return response.data;
  },

  // Admin operations
  updateUser: async (id, data) => {
    const response = await api.put(`/admin/users/${id}`, data);
    return response.data;
  },

  deleteUser: async (id) => {
    const response = await api.delete(`/admin/users/${id}`);
    return response.data;
  },

  bulkDeleteUsers: async (userIds) => {
    const response = await api.delete('/admin/users/bulk', {
      data: userIds,
    });
    return response.data;
  },
};

// Boat Types API
export const boatTypesAPI = {
  getBoatTypes: async () => {
    const response = await api.get('/boat-types/');
    return response.data;
  },

  getSidePreference: async (boatType) => {
    const response = await api.get(`/boat-types/side-preference/${boatType}`);
    return response.data;
  },
};

// Training Groups API
export const trainingGroupsAPI = {
  getGroups: async (include_crews = false) => {
    const response = await api.get('/training-groups/', {
      params: { include_crews },
    });
    return response.data;
  },

  // Admin endpoint to get all training groups
  getAllGroupsAdmin: async (include_crews = false) => {
    const response = await api.get('/training-groups/admin/all', {
      params: { include_crews },
    });
    return response.data;
  },

  // Public endpoint for registration (no auth required)
  getPublicGroups: async () => {
    const response = await api.get('/training-groups/public');
    return response.data;
  },

  // Get training groups available for application (excluding user's current groups)
  getAvailableGroupsForApplication: async () => {
    const response = await api.get('/training-groups/available-for-application');
    return response.data;
  },

  getGroup: async (id) => {
    const response = await api.get(`/training-groups/${id}/`);
    return response.data;
  },

  createGroup: async (groupData) => {
    const response = await api.post('/training-groups/', groupData);
    return response.data;
  },

  updateGroup: async (id, updateData) => {
    const response = await api.put(`/training-groups/${id}/`, updateData);
    return response.data;
  },

  deleteGroup: async (id) => {
    const response = await api.delete(`/training-groups/${id}/`);
    return response.data;
  },

  // Member management
  addMember: async (groupId, memberData) => {
    const response = await api.post(`/training-groups/${groupId}/members/`, memberData);
    return response.data;
  },

  updateMember: async (groupId, memberId, updateData) => {
    const response = await api.put(`/training-groups/${groupId}/members/${memberId}/`, updateData);
    return response.data;
  },

  removeMember: async (groupId, memberId) => {
    const response = await api.delete(`/training-groups/${groupId}/members/${memberId}/`);
    return response.data;
  },

  // Equipment management
  addEquipment: async (groupId, equipmentData) => {
    const response = await api.post(`/training-groups/${groupId}/equipment/`, equipmentData);
    return response.data;
  },

  removeEquipment: async (groupId, equipmentId) => {
    const response = await api.delete(`/training-groups/${groupId}/equipment/${equipmentId}/`);
    return response.data;
  },

  // Admin bulk remove members from group
  bulkRemoveMembers: async (groupId, userIds) => {
    const response = await api.delete(`/training-groups/admin/${groupId}/members/bulk`, {
      data: userIds,
    });
    return response.data;
  },
};

// Planning API
export const planningAPI = {
  // Core daily planning data endpoint
  getDailyPlanningData: async (date, crewIds = null) => {
    const params = { date };
    if (crewIds) params.crew_ids = crewIds;
    const response = await api.get('/planning/daily-data', { params });
    return response.data;
  },

  // Schedule Modal API endpoints
  getSessionConfiguration: async (sessionId) => {
    const response = await api.get(`/planning/sessions/configure/${sessionId}`);
    return response.data;
  },

  getRowerAvailability: async (sessionId, sessionStartTime, crewId = null) => {
    const params = {
      session_start_time_str: sessionStartTime,
    };
    if (crewId) params.crew_id = crewId;
    const response = await api.get(`/planning/sessions/${sessionId}/rowers/availability`, {
      params,
    });
    return response.data;
  },

  getCoachAvailability: async (sessionId, sessionStartTime, trainingGroupId = null) => {
    const params = {
      session_start_time_str: sessionStartTime,
    };
    if (trainingGroupId) params.training_group_id = trainingGroupId;
    const response = await api.get(`/planning/sessions/${sessionId}/coaches/availability`, {
      params,
    });
    return response.data;
  },

  validateSessionConfiguration: async (sessionId, configurationData) => {
    const response = await api.post(`/planning/sessions/${sessionId}/validate`, configurationData);
    return response.data;
  },

  saveSessionConfiguration: async (sessionId, configurationData) => {
    const response = await api.post(`/planning/sessions/${sessionId}/configure`, configurationData);
    return response.data;
  },

  getCrewLineupTemplates: async (crewId) => {
    const response = await api.get(`/planning/crews/${crewId}/lineup-templates`);
    return response.data;
  },

  // Session splitting endpoints
  scheduleSessionCrews: async (sessionId, scheduleData) => {
    const response = await api.post(`/planning/sessions/${sessionId}/schedule`, scheduleData);
    return response.data;
  },

  rescheduleSession: async (sessionId) => {
    const response = await api.put(`/planning/sessions/${sessionId}/reschedule`);
    return response.data;
  },

  markTaskSessionsScheduled: async (taskId) => {
    const response = await api.post(`/planning/tasks/${taskId}/mark-scheduled`);
    return response.data;
  },

  deleteSessionOrGroup: async (sessionId, deleteEntireGroup = false) => {
    const params = { delete_entire_group: deleteEntireGroup };
    const response = await api.delete(`/planning/sessions/${sessionId}`, { params });
    return response.data;
  },

  scheduleSessionWithLineups: async (scheduleData) => {
    const response = await api.post('/planning/schedule-session-with-lineups', scheduleData);
    return response.data;
  },

  // Check seating availability for a crew
  checkSeatingAvailability: async (crewId, sessionId, seating) => {
    const response = await api.post(`/planning/crews/${crewId}/check-seating-availability`, {
      session_id: sessionId,
      seating,
    });
    return response.data;
  },

  // Check rower availability for specific positions
  checkRowerAvailability: async (crewId, sessionId, rowerId, position) => {
    const response = await api.post(`/planning/crews/${crewId}/check-rower-availability`, {
      session_id: sessionId,
      rower_id: rowerId,
      position,
    });
    return response.data;
  },
};

// Autoplanner API
export const autoplannerAPI = {
  getAutoplannerTasks: async (viewMode = 'my-tasks') => {
    const params = { view_mode: viewMode };
    const response = await api.get('/autoplanner/tasks', { params });
    return response.data;
  },

  getAutoplannerTask: async (taskId) => {
    const response = await api.get(`/autoplanner/tasks/${taskId}`);
    return response.data;
  },

  // Fetch the final autoplanner result for a completed task
  getAutoplannerResult: async (taskId) => {
    const response = await api.get(`/autoplanner/tasks/${taskId}/result`);
    return response.data;
  },

  getSessionsByIds: async (sessionIds) => {
    const response = await api.get('/training-sessions', {
      params: { session_ids: sessionIds.join(',') },
    });
    return response.data;
  },

  createAutoplannerTask: async (trainingGroupId, selectedSessions, startDate, endDate) => {
    const params = {
      training_group_id: trainingGroupId,
      session_ids: selectedSessions.map((session) => session.id),
      start_date: startDate,
      end_date: endDate,
    };
    const response = await api.post('/autoplanner/tasks', params);
    return response.data;
  },

  updateAutoplannerTask: async (taskId, taskData, event) => {
    const params = {
      event,
      ...(taskData || {}),
    };
    const response = await api.put(`/autoplanner/tasks/${taskId}`, params);
    return response.data;
  },

  deleteAutoplannerTask: async (taskId) => {
    const response = await api.delete(`/autoplanner/tasks/${taskId}`);
    return response.data;
  },

  deleteTask: async (taskId) => {
    const response = await api.delete(`/autoplanner/tasks/${taskId}`);
    return response.data;
  },

  // New validation endpoints
  validateAvailability: async (taskId) => {
    const params = {
      task_id: taskId,
    };
    const response = await api.post('/autoplanner/validation/availability', params);
    return response.data;
  },

  validateSchedulingFeasibility: async (taskId) => {
    const params = {
      task_id: taskId,
    };
    const response = await api.post('/autoplanner/validation/scheduling-feasibility', params);
    return response.data;
  },

  validateSeatCapacityBalance: async (taskId) => {
    const params = {
      task_id: taskId,
    };
    const response = await api.post('/autoplanner/validation/seat-capacity-balance', params);
    return response.data;
  },

  // Excluded rowers per session management
  saveExcludedRowersPerSession: async (taskId, excludedRowers) => {
    const params = {
      excluded_rowers: excludedRowers,
    };
    const response = await api.post(
      `/autoplanner/tasks/${taskId}/excluded-rowers-per-session`,
      params
    );
    return response.data;
  },

  getExcludedRowersPerSession: async (taskId) => {
    const response = await api.get(`/autoplanner/tasks/${taskId}/excluded-rowers-per-session`);
    return response.data;
  },

  // prompt CRUD removed; only prompt_type remains in task updates

  // Session exclusion management
  excludeSessionForCrew: async (taskId, crewId, sessionId) => {
    const params = {
      crew_id: crewId,
      session_id: sessionId,
    };
    const response = await api.post(`/autoplanner/tasks/${taskId}/exclude-session`, params);
    return response.data;
  },

  excludeSessionsForCrew: async (taskId, crewId, sessionIds) => {
    const params = {
      crew_id: crewId,
      session_ids: sessionIds,
    };
    const response = await api.post(`/autoplanner/tasks/${taskId}/exclude-sessions`, params);
    return response.data;
  },

  includeSessionForCrew: async (taskId, crewId, sessionId) => {
    const params = {
      crew_id: crewId,
      session_id: sessionId,
    };
    const response = await api.post(`/autoplanner/tasks/${taskId}/include-session`, params);
    return response.data;
  },

  includeSessionsForCrew: async (taskId, crewId, sessionIds) => {
    const params = {
      crew_id: crewId,
      session_ids: sessionIds,
    };
    const response = await api.post(`/autoplanner/tasks/${taskId}/include-sessions`, params);
    return response.data;
  },

  // Resolution management
  storeResolutions: async (taskId, resolutions) => {
    const params = {
      resolutions,
    };
    const response = await api.post(`/autoplanner/tasks/${taskId}/resolutions`, params);
    return response.data;
  },

  getResolutions: async (taskId) => {
    const response = await api.get(`/autoplanner/tasks/${taskId}/resolutions`);
    return response.data;
  },

  sendAvailabilityReminder: async (taskId, userId) => {
    const params = {
      user_id: userId,
    };
    const response = await api.post(`/autoplanner/tasks/${taskId}/send-reminder`, params);
    return response.data;
  },

  getSentReminders: async (taskId) => {
    const response = await api.get(`/autoplanner/tasks/${taskId}/sent-reminders`);
    return response.data;
  },

  // Task management
  saveTaskDraft: async (taskId, taskData) => {
    const response = await api.put(`/autoplanner/tasks/${taskId}`, taskData);
    return response.data;
  },

  fixTaskSessions: async (taskId, sessionIds) => {
    const params = {
      session_ids: sessionIds,
    };
    const response = await api.post(`/autoplanner/tasks/${taskId}/fix-sessions`, params);
    return response.data;
  },

  // Normalization management
  createNormalizedDataset: async (taskId) => {
    const response = await api.post(`/autoplanner/tasks/${taskId}/normalize`);
    return response.data;
  },

  // Excluded rowers per session management
  saveExcludedRowersPerSession: async (taskId, excludedRowers) => {
    const params = {
      excluded_rowers: excludedRowers,
    };
    const response = await api.post(
      `/autoplanner/tasks/${taskId}/excluded-rowers-per-session`,
      params
    );
    return response.data;
  },

  getExcludedRowersPerSession: async (taskId) => {
    const response = await api.get(`/autoplanner/tasks/${taskId}/excluded-rowers-per-session`);
    return response.data;
  },

  getNormalizedDataset: async (taskId) => {
    const response = await api.get(`/autoplanner/tasks/${taskId}/normalized-data`);
    return response.data;
  },

  // Locked seatings management
  saveLockedSeatings: async (taskId, lockedSeatings) => {
    const params = {
      locked_seatings: lockedSeatings,
    };
    const response = await api.post(`/autoplanner/tasks/${taskId}/locked-seatings`, params);
    return response.data;
  },

  getLockedSeatings: async (taskId) => {
    const response = await api.get(`/autoplanner/tasks/${taskId}/locked-seatings`);
    return response.data;
  },

  validateLockedSeatings: async (taskId, lockedSeatings) => {
    const params = {
      locked_seatings: lockedSeatings,
    };
    const response = await api.post(
      `/autoplanner/tasks/${taskId}/validate-locked-seatings`,
      params
    );
    return response.data;
  },

  checkSeatingAvailability: async (taskId, crewId, sessionId, seating) => {
    const params = {
      crew_id: crewId,
      session_id: sessionId,
      seating,
    };
    const response = await api.post(
      `/autoplanner/tasks/${taskId}/check-seating-availability`,
      params
    );
    return response.data;
  },

  saveSessionOrder: async (taskId, sessionOrder) => {
    const params = {
      session_order: sessionOrder,
    };
    const response = await api.post(`/autoplanner/tasks/${taskId}/session-order`, params);
    return response.data;
  },

  // Session-level availability overrides
  saveSessionOverrides: async (taskId, sessionId, payload) => {
    const response = await api.post(
      `/autoplanner/tasks/${taskId}/sessions/${sessionId}/overrides`,
      payload
    );
    return response.data;
  },
  getSessionOverrides: async (taskId, sessionId) => {
    const response = await api.get(`/autoplanner/tasks/${taskId}/sessions/${sessionId}/overrides`);
    return response.data;
  },
  clearSessionOverrides: async (taskId, sessionId) => {
    const response = await api.delete(
      `/autoplanner/tasks/${taskId}/sessions/${sessionId}/overrides`
    );
    return response.data;
  },

  // Crew-specific session overrides
  getSessionOverridesForCrew: async (taskId, sessionId, crewId) => {
    const response = await api.get(
      `/autoplanner/tasks/${taskId}/sessions/${sessionId}/crews/${crewId}/overrides`
    );
    return response.data;
  },
  clearSessionOverridesForCrew: async (taskId, sessionId, crewId) => {
    const response = await api.delete(
      `/autoplanner/tasks/${taskId}/sessions/${sessionId}/crews/${crewId}/overrides`
    );
    return response.data;
  },

  // Admin endpoint to get all tasks
  getAllTasksAdmin: async () => {
    const response = await api.get('/autoplanner/admin/all-tasks');
    return response.data;
  },

  // Admin bulk delete tasks
  bulkDeleteTasks: async (taskIds) => {
    const response = await api.delete('/autoplanner/admin/bulk', {
      data: taskIds,
    });
    return response.data;
  },
};

// Dashboard API
export const dashboardAPI = {
  getDashboardData: async () => {
    const localDate = new Date().toLocaleDateString('en-CA');
    const response = await api.get('/dashboard/', { params: { local_date: localDate } });
    return response.data;
  },
};

// Calendar API
export const calendarAPI = {
  getCalendarUuid: async () => {
    const response = await api.get('/calendar/uuid');
    return response.data;
  },

  refreshCalendarUuid: async () => {
    const response = await api.post('/calendar/uuid/refresh');
    return response.data;
  },
};

// Calendar Integration API
export const calendarIntegrationAPI = {
  // Get all calendar integrations for the current user
  getIntegrations: async () => {
    const response = await api.get('/calendar-integration/');
    return response.data;
  },

  // Create a new calendar integration
  createIntegration: async (integrationData) => {
    const response = await api.post('/calendar-integration/', integrationData);
    return response.data;
  },

  // Get a specific calendar integration
  getIntegration: async (integrationId) => {
    const response = await api.get(`/calendar-integration/${integrationId}`);
    return response.data;
  },

  // Update a calendar integration
  updateIntegration: async (integrationId, updateData) => {
    const response = await api.put(`/calendar-integration/${integrationId}`, updateData);
    return response.data;
  },

  // Delete a calendar integration
  deleteIntegration: async (integrationId) => {
    const response = await api.delete(`/calendar-integration/${integrationId}`);
    return response.data;
  },

  // Get calendar events for a specific week
  getWeeklyEvents: async (weekStart) => {
    const response = await api.get(`/calendar-integration/events/week/${weekStart}`);
    return response.data;
  },

  // Test a calendar integration
  testIntegration: async (integrationId) => {
    const response = await api.post(`/calendar-integration/${integrationId}/test`);
    return response.data;
  },
};

export { api };
export default api;
